import qs from 'querystring';
import React, { FC, ReactChild, useMemo, useState } from 'react';
import * as Yup from 'yup';
import _head from 'lodash/head';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import FormikInput from '@minecraft.formik-input';
import FormikPassword from '@minecraft.formik-password';
import Label from '@minecraft.label';
import UncontrolledAlert from '@minecraft.uncontrolled-alert';
import { getEnvironment } from '@minecraft.environment';
import { CLAIM_ACCOUNT_STEP, ModalStepProps, Instance, Error } from '../types';
import { getClosedOrLockedErrorMessage } from '../../../utils';
import { CLAIM_ACCOUNT } from './ModalBodyGraphQL';
import { useValidationMessages } from './constants';
import { StyledForm, FieldWrapper, StyledButton, StyledLinkButton } from './ModalBodyStyles';

const onboardingUrl = getEnvironment()?.ONBOARDING_URL;

type Props = {
  migratedAccountID: number;
  token: string;
} & Pick<Instance, 'instance'> &
  Pick<ModalStepProps, 'setStep'>;

const Login: FC<Props> = ({ instance, setStep, migratedAccountID, token }) => {
  const { t } = useTranslation();

  const validationMessages = useValidationMessages();

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      username: Yup.string().required(validationMessages.EMPTY_FIELD_ERROR),
      password: Yup.string().required(validationMessages.EMPTY_FIELD_ERROR),
    });
  }, [validationMessages]);

  const [claimAccount, { loading }] = useMutation(CLAIM_ACCOUNT);
  const [error, setError] = useState<ReactChild>();

  const handleError = (errData: Error): void => {
    switch (errData?.data?.errorCode) {
      case 20:
        setError(getClosedOrLockedErrorMessage(t, errData, false) || errData.message);
        break;
      case 25: {
        setError(errData?.message || validationMessages.MAX_NUMBERS_CLAIMED_ACC);
        break;
      }
      default:
        setError(errData?.message);
        break;
    }
  };

  const closeError = (): void => setError('');

  return (
    <Formik
      initialValues={{ username: '', password: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        try {
          const data = await claimAccount({
            variables: {
              ...values,
              instance: instance.value,
              migratedAccountID,
            },
          });
          const params = qs.stringify({
            username: values.username,
            audienceToken: token,
            bauLogin: data?.data?.bauLogin,
          });

          const dataErrors = (data as any).errors;

          if (Array.isArray(dataErrors) && dataErrors.length) {
            handleError(dataErrors[0]);
          } else {
            window.location.replace(`${onboardingUrl}/claim-additional-account?${params}`);
          }
        } catch (e) {
          handleError(_head(e.graphQLErrors));
        }
      }}
    >
      {({ isValid }) => {
        return (
          <StyledForm>
            <p>{t('common:claimAdditionalAccounts.formTitle')}</p>
            <h4>{instance?.item}</h4>
            {Boolean(error) && (
              <UncontrolledAlert toggle={closeError} color="warning">
                {error}
              </UncontrolledAlert>
            )}
            <FieldWrapper>
              <Label for="username">{t('common:form.label.usernameRequired')}</Label>
              <FormikInput name="username" type="text" />
            </FieldWrapper>
            <FieldWrapper>
              <Label for="password">{t('common:form.label.passwordRequired')}</Label>
              <FormikPassword name="password" showText={t('common:button.show')} hideText={t('common:button.hide')} />
            </FieldWrapper>
            <StyledButton type="submit" disabled={!isValid || loading}>
              {t('common:button.claimAccount')}
            </StyledButton>
            <StyledLinkButton type="button" color="link" onClick={() => setStep(CLAIM_ACCOUNT_STEP.FORGOT_PASSWORD)}>
              {t('common:button.forgotPassword')}
            </StyledLinkButton>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export default Login;
